import { createContext, useContext } from 'react';
import type { ChordTypeId } from '@/music/chords';
import type { StringSet } from '@/music/tuning';
import type { Fingering } from '@/music/voicing';
import type { PinLane } from '@/music/voicingPins';

/** The chord a grip was voiced FOR, as the engine saw it — what the pin
 * picker enumerates alternatives from (voicingPins.ts). `chordType` is the
 * engine's input type, which is what a pin is filed under. */
export interface InspectPinChord {
  root: string;
  displayRoot?: string;
  chordType: ChordTypeId;
  targetTopNote?: string | null;
  targetTopMidi?: number | null;
  voicingNotes?: string[];
  bass?: string;
}

/** Where a grip sits in the tune's arrangement: the lane it was drawn in and
 * its position key, so the inspector can pin an alternative in its place. */
export interface InspectPinContext {
  lane: PinLane;
  key: string;
  chord: InspectPinChord;
  pinned: boolean;
  /** Piano lanes: the family the comping walk enumerates. */
  pianoFamily?: string;
}

export interface InspectChord {
  fingering: Fingering;
  rootDisplay: string;
  /** A source grip need not fit one of the generated four-note formulas. */
  chordType?: ChordTypeId;
  symbol: string;
  inversion?: number;
  stringSet: StringSet;
  targetTopNote?: string | null;
  leadNote?: string | null;
  title?: string;
  pin?: InspectPinContext;
  source?: {
    reference: string;
    description?: string;
    /** Exact sounding note spellings with octave, indexed low E to high E. */
    noteNames: readonly (string | null)[];
  };
}

/** A keyboard voicing — the piano sheets' and overlays' grips. */
export interface InspectKeyboardChord {
  kind: 'piano';
  midis: number[];
  hands?: { left: number[]; right: number[] };
  rootDisplay: string;
  chordType: ChordTypeId;
  symbol: string;
  title?: string;
  pin?: InspectPinContext;
}

export type Inspectable = InspectChord | InspectKeyboardChord;

export function isKeyboardChord(chord: Inspectable): chord is InspectKeyboardChord {
  return 'kind' in chord && chord.kind === 'piano';
}

export interface InspectorApi {
  inspect: (chord: Inspectable) => void;
}

export const InspectorContext = createContext<InspectorApi | null>(null);

export function useInspector(): InspectorApi {
  const ctx = useContext(InspectorContext);
  if (!ctx) throw new Error('useInspector must be used within InspectorProvider');
  return ctx;
}
